import { useState } from 'react'
import {
  Box, Button, Card, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, Tooltip, Chip,
} from '@mui/material'
import { ExpandMore, ExpandLess } from '@mui/icons-material'
import { UTIL_BANDS, utilBand, lineUtilization, overallUtilization, dosRiskPct } from './fourToTwelve'
import { frozenWidth, monthWidth, tableWidth } from './gridLayout'

const LINES = ['LINE 1', 'LINE 2', 'LINE 3', 'LINE 4']
const SLATE = '#475569'

const pct = (v) => (v == null ? '—' : `${Math.round(v)}%`)

// Capacity and Risk. Collapsible (expanded by default).
// - Next 3 Months (FAR-77, the default): a row per line; clicking one filters
//   the SKU Plan to that primary line.
// - Months 4–18 (FAR-83 §3, overall): one Overall Capacity row across all
//   lines; no Warehouse row; no row is clickable.
// Month columns alternate a light blue tint (FAR-77/83 §2), header included.
//
// FAR-83 §3 / FAR-77 §3 and the mockup: the header row holds Hide/Show at the
// left, the title, the compact legend, and the month headers. Hide collapses
// the body; the header row (with the months) stays.
const TINT = '#F0F7FF'
const HEAD = '#F1F5F9' // header row (slate-50)
const HEAD_TINT = '#E6EEF8' // header row on a tinted month
const tint = (i) => (i % 2 === 1 ? { bgcolor: TINT } : {})
// FAR-83 #13: each month cell starts with the SKU grid's 2px month border.
const monthCell = (i) => ({ borderLeft: '2px solid', borderLeftColor: 'divider', ...tint(i) })
// The legend's compact labels, as in the mockup: <80, 80–90, 90–100, >100.
const LEGEND = { under: '<80', good: '80–90', caution: '90–100', over: '>100' }

export default function CapacityRiskCard({
  rows, months, capacity, workingDays, activeLine, onLineClick, overall = false, scrollRef,
}) {
  const [open, setOpen] = useState(true)
  const capByLine = Object.fromEntries((capacity?.lines || []).map((l) => [l.line, l]))
  // FAR-83 #13: the label column is as wide as the SKU grid's frozen columns
  // and each month as wide as its four sub-columns, so the months line up.
  const withLines = !overall
  // Sticky label cell; on the active line row it keeps the row's highlight.
  // action.selected is see-through, so it is layered over a solid paper
  // background; otherwise months would show through when scrolled under it.
  const label = (active = false) => ({
    textAlign: 'left !important', fontWeight: 600, position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper',
    ...(active && { backgroundImage: (t) => `linear-gradient(${t.palette.action.selected}, ${t.palette.action.selected})` }),
  })

  return (
    // Plain, not outlined: an outlined card's 1px right border would make it
    // 1px narrower inside than the SKU card.
    <Card sx={{ borderLeft: `4px solid ${SLATE}`, boxShadow: 0 }} data-testid="capacity-risk-card">
      {/* Scrolls with the SKU grid (useSyncedScrollX). The stable gutter
          matches the grid's, so both reach the same right end. */}
      <Box ref={scrollRef} sx={{ overflowX: 'auto', scrollbarGutter: 'stable' }}>
        <Table size="small" sx={{
          tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, width: tableWidth(withLines, months.length),
          '& td, & th': { textAlign: 'center', py: 0.5, overflow: 'hidden' },
        }}>
          <colgroup>
            <col style={{ width: frozenWidth(withLines) }} />
            {months.map((m) => <col key={m.key} style={{ width: monthWidth() }} />)}
          </colgroup>
          <TableHead>
            <TableRow data-testid="capacity-header">
              <TableCell sx={{ ...label(), bgcolor: HEAD, py: '6px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined" color="inherit" onClick={() => setOpen((v) => !v)}
                    startIcon={open ? <ExpandLess /> : <ExpandMore />} aria-expanded={open}
                    aria-label={open ? 'Hide Capacity and Risk' : 'Show Capacity and Risk'}
                    sx={{ borderRadius: 4, py: 0, px: 1, minWidth: 0, textTransform: 'none', fontSize: '0.75rem', bgcolor: 'background.paper', borderColor: 'divider' }}>
                    {open ? 'Hide' : 'Show'}
                  </Button>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: SLATE }}>
                    Capacity and Risk
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} aria-label="Utilization legend" data-testid="capacity-legend">
                    {UTIL_BANDS.map((b) => (
                      <Tooltip key={b.key} title={b.label}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <Box sx={{ width: 10, height: 10, bgcolor: b.color, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{LEGEND[b.key] || b.label}</Typography>
                        </Box>
                      </Tooltip>
                    ))}
                  </Box>
                  {capacity?.placeholder && (
                    <Tooltip title="Line hours are placeholder values, not Faribault's. Set real hours in Admin → Capacity.">
                      <Chip label="Placeholder capacity" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              {months.map((m, i) => (
                <TableCell key={m.key} data-month-head={m.key}
                  sx={{ fontWeight: 700, ...monthCell(i), bgcolor: i % 2 === 1 ? HEAD_TINT : HEAD }}>
                  {m.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {open && (
            <TableBody data-testid="capacity-body">
              {overall && (
                <TableRow data-testid="overall-capacity-row">
                  <TableCell sx={label()}>Overall Capacity</TableCell>
                  {months.map((m, i) => {
                    const v = overallUtilization(rows, m.key, capacity?.lines, workingDays?.[m.iso])
                    const band = utilBand(v)
                    return (
                      <TableCell key={m.key} sx={monthCell(i)}>
                        <Box component="span" sx={{ display: 'inline-block', px: 1, borderRadius: 1, bgcolor: band?.color, color: band?.text, fontWeight: 600 }}>
                          {pct(v)}
                        </Box>
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              {!overall && LINES.map((line) => {
                const active = activeLine === line
                return (
                  <TableRow key={line} hover onClick={() => onLineClick?.(active ? '' : line)}
                    sx={{ cursor: 'pointer', bgcolor: active ? 'action.selected' : undefined }}
                    aria-selected={active}>
                    <TableCell sx={label(active)}>
                      {line.replace('LINE', 'Line')}
                    </TableCell>
                    {months.map((m, i) => {
                      const v = lineUtilization(rows, line, m.key, capByLine[line], workingDays?.[m.iso])
                      const band = utilBand(v)
                      return (
                        <TableCell key={m.key} sx={monthCell(i)}>
                          <Box component="span" sx={{ display: 'inline-block', px: 1, borderRadius: 1, bgcolor: band?.color, color: band?.text, fontWeight: 600 }}>
                            {pct(v)}
                          </Box>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
              {[['below', '% Below Min DOS', '#DC2626'], ['above', '% Above Max DOS', '#D97706']].map(([which, text, color]) => (
                <TableRow key={which}>
                  <TableCell sx={label()}>{text}</TableCell>
                  {months.map((m, i) => {
                    const v = dosRiskPct(rows, m.key, which)
                    return (
                      <TableCell key={m.key} sx={monthCell(i)}>
                        <Box sx={{ fontWeight: 600 }}>{pct(v)}</Box>
                        <Box sx={{ height: 4, bgcolor: 'action.hover', borderRadius: 2, mt: 0.25 }}>
                          <Box sx={{ height: 4, width: `${Math.min(100, v || 0)}%`, bgcolor: color, borderRadius: 2 }} />
                        </Box>
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </Box>
    </Card>
  )
}
